import React, { useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import "./oncoKB.scss";
import { getVarsoUrl } from "../../../../common/helpers/helpers";
import HelperText from "../../../../components/atoms/HelperText";
import StyledComponentPanel from "../../../../components/atoms/ComponentPanel/StyledComponentPanel";
import {
  colorSelect,
  colorSelectDiagnostic,
  colorSelectPrognostic,
  diagnosticIcon,
  fdaIcon,
  handleDiagnosticLevels,
  handleFDALevels,
  handlePrognosticLevels,
  handleTherapeuticLevels,
  numberOfDiagnosticSvg,
  numberOfFDASvg,
  numberOfPrognosticSvg,
  numberOfTherapeuticSvg,
  oncogenicityIcon,
  prognosticIcon,
  therapeuticIcon,
  regexTransformation,
} from "./oncoKBHelpers";
import {
  diagnosticLevels,
  fdaLevels,
  prognosticLevels,
  therapeuticLevels,
  oncogenicityLevels,
} from "./oncoKBTooltips";
import PublicationLinks from "../../../../components/Publications/components/PublicationLinks/PublicationLinks";
import { getOncoKBData } from "../../../../state/selectors/OncoKB.selectors";
import { getVariantGeneralInfo } from "../../../../state/selectors/CardsUI.selectors";
import LinkifyBase from "../../../../components/social/LinkifyBase/LinkifyBase";
import ClickTooltip from "../../../../components/atoms/Tooltip/ClickTooltip";
import isWithinClass from "../../../../common/helpers/isWithinClass";
import { getIsFLT3ITD } from "../../../../state/selectors/general.selectors";
import TruncatedText from "../../../../components/TruncatedText/TruncatedText";
import { truncateHgvs } from "../../../../components/TruncatedText/helpers";
import ProteinValue from "../ProteinValue/ProteinValue";

const OncoKBVariant = ({ oncoKBData, variantGeneralInfo, isFLT3ITD }) => {
  const [activeTab, setActiveTab] = useState(1);
  const { version } = oncoKBData[0] || {};

  const handleTabClick = (tabIndex) => {
    setActiveTab(tabIndex);
  };

  return (
    <div data-testid="variant-oncokb">
      <div className="">
        <StyledComponentPanel
          title={
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-x-4">
              <div className="tw-font-bold">
                <a
                  href={getVarsoUrl("https://www.oncokb.org")}
                  target="_blank"
                  className="url-title"
                >
                  OncoKB
                </a>
                <HelperText customText="OncoKB™ is a precision oncology knowledge base developed at Memorial Sloan Kettering Cancer Center that contains biological and clinical information about genomic alterations in cancer." />
              </div>
              {!isFLT3ITD && variantGeneralInfo ? (
                <small className="tw-font-bold">
                  {variantGeneralInfo?.hgvsCdot ? (
                    <span>{variantGeneralInfo.hgvsCdot}</span>
                  ) : null}
                  {variantGeneralInfo?.pValue ? (
                    <span>{variantGeneralInfo.pValue}</span>
                  ) : null}
                </small>
              ) : null}
              {isFLT3ITD && variantGeneralInfo ? (
                <span className="tw-font-bold tw-no-underline">
                  {variantGeneralInfo?.hgvsCdot ? (
                    <TruncatedText
                      truncatedText={truncateHgvs(variantGeneralInfo?.hgvsCdot)}
                      originalText={variantGeneralInfo?.hgvsCdot}
                    />
                  ) : null}
                  {variantGeneralInfo?.pValue ? (
                    <ProteinValue
                      isFLT3ITD={isFLT3ITD}
                      pValue={variantGeneralInfo?.pValue}
                    />
                  ) : null}
                </span>
              ) : null}
              {version ? (
                <small>Version: {oncoKBData[0]?.version}</small>
              ) : null}
              <div className="tw-flex-grow" />
              <small>
                <a
                  href={getVarsoUrl(
                    `https://www.oncokb.org/gene/${oncoKBData[0]?.query?.hugosymbol}/${oncoKBData[0]?.query?.alteration}`
                  )}
                  target="_blank"
                >
                  View full records on OncoKB
                </a>
              </small>
            </div>
          }
        >
          <div className="tw-p-4 tw-bg-white" data-testid="oncokb-findings">
            <div className="tw-font-bold">
              OncoKB findings for {oncoKBData[0]?.query?.hugosymbol}/
              {oncoKBData[0]?.query?.alteration}{" "}
              <HelperText
                RenderContent={() => oncogenicityLevels}
                title="Explanation for Oncogenicity Symbols"
              />
            </div>
            {oncoKBData[0]?.oncogenic}{" "}
            {oncogenicityIcon(oncoKBData[0]?.oncogenic, "25px")} |{" "}
            <span className="tw-font-bold" data-testid="mutation-effect">
              {oncoKBData[0]?.mutation_effect?.knowneffect}
            </span>{" "}
            |{" "}
            {oncoKBData[0]?.highest_sensitive_level ? (
              <span data-testid="therapeutic-levels-and-icon">
                {handleTherapeuticLevels(
                  oncoKBData[0]?.highest_sensitive_level
                )}{" "}
                {therapeuticIcon(
                  colorSelect(oncoKBData[0]?.highest_sensitive_level),
                  "30px",
                  numberOfTherapeuticSvg(oncoKBData[0]?.highest_sensitive_level)
                )}{" "}
                |
              </span>
            ) : null}{" "}
            {oncoKBData[0]?.highest_resistance_level ? (
              <span data-testid="resistance-levels-and-icon">
                {handleTherapeuticLevels(
                  oncoKBData[0]?.highest_resistance_level
                )}{" "}
                {therapeuticIcon(
                  colorSelect(oncoKBData[0]?.highest_resistance_level),
                  "30px",
                  numberOfTherapeuticSvg(
                    oncoKBData[0]?.highest_resistance_level
                  )
                )}{" "}
                |
              </span>
            ) : null}{" "}
            {oncoKBData[0]?.highest_diagnostic_implication_level ? (
              <span data-testid="diagnostic-levels-and-icon">
                {handleDiagnosticLevels(
                  oncoKBData[0]?.highest_diagnostic_implication_level
                )}{" "}
                {diagnosticIcon(
                  colorSelectDiagnostic(
                    oncoKBData[0]?.highest_diagnostic_implication_level
                  ),
                  "30px",
                  numberOfDiagnosticSvg(
                    oncoKBData[0]?.highest_diagnostic_implication_level
                  )
                )}{" "}
                |
              </span>
            ) : null}{" "}
            {oncoKBData[0]?.highest_prognostic_implication_level ? (
              <span data-testid="prognostic-levels-and-icon">
                {handlePrognosticLevels(
                  oncoKBData[0]?.highest_prognostic_implication_level
                )}{" "}
                {prognosticIcon(
                  colorSelectPrognostic(
                    oncoKBData[0]?.highest_prognostic_implication_level
                  ),
                  "30px",
                  numberOfPrognosticSvg(
                    oncoKBData[0]?.highest_prognostic_implication_level
                  )
                )}{" "}
                |
              </span>
            ) : null}{" "}
            {oncoKBData[0]?.highest_fda_level ? (
              <span data-testid="fda-levels-and-icon">
                {handleFDALevels(oncoKBData[0]?.highest_fda_level)}{" "}
                {fdaIcon(
                  "30px",
                  numberOfFDASvg(oncoKBData[0]?.highest_fda_level)
                )}
              </span>
            ) : null}
            <br />
            <div data-testid="gene-summary">
              <br />
              {oncoKBData[0]?.gene_summary}
            </div>
            <br />
            <div data-testid="variant-summary">
              {oncoKBData[0]?.variant_summary}
            </div>
            <br />
            <b>Mutation effect description</b>
            <div data-testid="mutation-effect-description">
              <LinkifyBase
                txt={regexTransformation(
                  oncoKBData[0]?.mutation_effect?.description
                )}
              />
            </div>
            <br />
            <br />
            <div className="tabs" data-testid="oncokb-tabs-section">
              <button
                type="button"
                onClick={() => handleTabClick(1)}
                className={activeTab === 1 ? "active" : ""}
              >
                Therapeutic
              </button>
              <button
                type="button"
                onClick={() => handleTabClick(2)}
                className={activeTab === 2 ? "active" : ""}
              >
                Diagnostic
              </button>
              <button
                type="button"
                onClick={() => handleTabClick(3)}
                className={activeTab === 3 ? "active" : ""}
              >
                Prognostic
              </button>
              <button
                type="button"
                onClick={() => handleTabClick(4)}
                className={activeTab === 4 ? "active" : ""}
              >
                FDA-Recognized Content
              </button>
            </div>
            <div className="tab-content" data-testid="oncokb-table-content">
              {activeTab === 1 && oncoKBData[0]?.highest_sensitive_level && (
                <div className="tab-panel">
                  <table className="oncokb_table">
                    <thead>
                      <tr>
                        <th>
                          Level
                          <HelperText
                            RenderContent={() => therapeuticLevels}
                            title="Explanation for Symbols of Therapeutic Levels"
                          />
                        </th>
                        <th>Alterations</th>
                        <th>
                          Level-Associated Cancer Types{" "}
                          <HelperText
                            RenderContent={() => (
                              <div>
                                The cancer type is curated using{" "}
                                <a href="https://oncotree.info/#/home">
                                  Oncotree.
                                </a>
                              </div>
                            )}
                          />
                        </th>
                        <th>Drugs</th>
                        <th>Publications</th>
                      </tr>
                    </thead>
                    <tbody>
                      {oncoKBData[0]?.treatments?.map((treatment, index) => (
                        <tr key={index}>
                          <td>
                            {therapeuticIcon(
                              colorSelect(treatment?.level),
                              "38px",
                              numberOfTherapeuticSvg(treatment?.level)
                            )}
                          </td>
                          <td>{treatment?.alterations.join(", ") || "N/A"}</td>
                          <td>
                            {treatment?.level_associated_cancer_type?.main_type
                              ?.name || "N/A"}
                          </td>
                          <td>
                            {treatment?.drugs && (
                              <ClickTooltip
                                ContentRenderer={() => (
                                  <LinkifyBase
                                    txt={regexTransformation(
                                      treatment?.description
                                    )}
                                  />
                                )}
                                checkPreventClose={(e) => {
                                  return isWithinClass(
                                    e.target,
                                    "prevent-tooltip-close"
                                  );
                                }}
                                zIndex={1}
                              >
                                <span className="tw-cursor-pointer tw-text-primary">
                                  {treatment?.drugs
                                    .map((drug) => drug?.drug_name)
                                    ?.join(", ") || "N/A"}
                                </span>
                              </ClickTooltip>
                            )}
                          </td>
                          <td>
                            {<PublicationLinks ids={treatment?.pmids} /> ||
                              "N/A"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {activeTab === 1 && !oncoKBData[0]?.highest_sensitive_level && (
                <div>No available data for therapeutic level of evidence.</div>
              )}

              {activeTab === 2 &&
                oncoKBData[0]?.highest_diagnostic_implication_level && (
                  <div className="tab-panel">
                    <table className="oncokb_table">
                      <thead>
                        <tr>
                          <th>
                            Level{" "}
                            <HelperText
                              RenderContent={() => diagnosticLevels}
                              title="Explanation for Symbols of Diagnostic Levels"
                            />
                          </th>
                          <th>Alterations</th>
                          <th>
                            Level-Associated Cancer Types{" "}
                            <HelperText
                              RenderContent={() => (
                                <div>
                                  The cancer type is curated using{" "}
                                  <a href="https://oncotree.info/#/home">
                                    Oncotree.
                                  </a>
                                </div>
                              )}
                            />
                          </th>
                          <th>Publications</th>
                        </tr>
                      </thead>
                      <tbody>
                        {oncoKBData[0]?.diagnostic_implications?.map(
                          (implication, index) => (
                            <tr key={index}>
                              <td>
                                {diagnosticIcon(
                                  colorSelectDiagnostic(
                                    implication?.level_of_evidence
                                  ),
                                  "38px",
                                  numberOfDiagnosticSvg(
                                    implication?.level_of_evidence
                                  )
                                )}
                              </td>
                              <td>
                                {implication?.alterations?.join(", ") || "N/A"}
                              </td>
                              <td>{implication?.tumor_type?.name}</td>
                              <td>
                                <PublicationLinks ids={implication?.pmids} />
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              {activeTab === 2 &&
                !oncoKBData[0]?.highest_diagnostic_implication_level && (
                  <div>No available data for diagnostic level of evidence.</div>
                )}
              {activeTab === 3 &&
                oncoKBData[0]?.highest_prognostic_implication_level && (
                  <div className="tab-panel">
                    <table className="oncokb_table">
                      <thead>
                        <tr>
                          <th>
                            Level{" "}
                            <HelperText
                              RenderContent={() => prognosticLevels}
                              title="Explanation for Symbols of Prognostic Levels"
                            />
                          </th>
                          <th>Alterations</th>
                          <th>
                            Level-Associated Cancer Types{" "}
                            <HelperText
                              RenderContent={() => (
                                <div>
                                  The cancer type is curated using{" "}
                                  <a href="https://oncotree.info/#/home">
                                    Oncotree.
                                  </a>
                                </div>
                              )}
                            />
                          </th>
                          <th>Publications</th>
                        </tr>
                      </thead>
                      <tbody>
                        {oncoKBData[0]?.prognostic_implications?.map(
                          (implication, index) => (
                            <tr key={index}>
                              <td>
                                {prognosticIcon(
                                  colorSelectPrognostic(
                                    implication?.level_of_evidence
                                  ),
                                  "38px",
                                  numberOfPrognosticSvg(
                                    implication?.level_of_evidence
                                  )
                                )}
                              </td>
                              <td>
                                {implication?.alterations?.join(", ") || "N/A"}
                              </td>
                              <td>{implication?.tumor_type?.name}</td>
                              <td>
                                <PublicationLinks ids={implication?.pmids} />
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              {activeTab === 3 &&
                !oncoKBData[0]?.highest_prognostic_implication_level && (
                  <div>No available data for prognostic level of evidence.</div>
                )}

              {activeTab === 4 && oncoKBData[0]?.highest_fda_level && (
                <div className="tab-panel">
                  <table className="oncokb_table">
                    <thead>
                      <tr>
                        <th style={{ width: "25%" }}>
                          FDA Level of Evidence{" "}
                          <HelperText
                            RenderContent={() => fdaLevels}
                            title="Explanation for Symbols of FDA Levels"
                          />
                        </th>
                        <th>Alteration</th>
                        <th>Cancer Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {oncoKBData[0]?.treatments?.map((treatment, index) => (
                        <tr key={index}>
                          <td>
                            {fdaIcon(
                              "38px",
                              numberOfFDASvg(treatment?.fda_level)
                            )}
                          </td>
                          <td>
                            {treatment?.alterations[
                              treatment?.alterations?.length - 1
                            ] || "N/A"}
                          </td>
                          <td>
                            {
                              treatment?.level_associated_cancer_type?.main_type
                                ?.name
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {activeTab === 4 && !oncoKBData[0]?.highest_fda_level && (
                <div>No available data for FDA level of evidence.</div>
              )}
            </div>
          </div>
        </StyledComponentPanel>
      </div>
    </div>
  );
};

OncoKBVariant.propTypes = {
  oncoKBData: PropTypes.arrayOf(
    PropTypes.shape({
      version: PropTypes.string,
      diagnostic_summary: PropTypes.string,
      gene_summary: PropTypes.string,
      variant_summary: PropTypes.string,
      highest_diagnostic_implication_level: PropTypes.string,
      highest_fda_level: PropTypes.string,
      highest_prognostic_implication_level: PropTypes.string,
      highest_resistance_level: PropTypes.string,
      highest_sensitive_level: PropTypes.string,
      oncogenic: PropTypes.string,
      query: PropTypes.shape({
        tumortype: PropTypes.string,
        alteration: PropTypes.string.isRequired,
        entrezgeneid: PropTypes.number,
        hugosymbol: PropTypes.string.isRequired,
        referencegenome: PropTypes.string,
      }),
      mutation_effect: PropTypes.shape({
        citations: PropTypes.shape({
          abstracts: PropTypes.arrayOf(PropTypes.string),
          pmids: PropTypes.arrayOf(PropTypes.string),
          description: PropTypes.string,
        }),
        description: PropTypes.string,
        knowneffect: PropTypes.string,
      }),
      treatments: PropTypes.arrayOf(
        PropTypes.shape({
          level: PropTypes.string,
          alterations: PropTypes.arrayOf(PropTypes.string),
          level_associated_cancer_type: PropTypes.shape({
            name: PropTypes.string,
          }),
          drugs: PropTypes.arrayOf(
            PropTypes.shape({
              drugName: PropTypes.string,
              description: PropTypes.string,
            })
          ),
          pmids: PropTypes.arrayOf(PropTypes.string),
        })
      ),
      diagnostic_implications: PropTypes.arrayOf(
        PropTypes.shape({
          level_of_evidence: PropTypes.string,
          alterations: PropTypes.arrayOf(PropTypes.string),
          tumor_type: PropTypes.shape({
            name: PropTypes.string,
          }),
          pmids: PropTypes.arrayOf(PropTypes.string),
        })
      ),
      prognostic_implications: PropTypes.arrayOf(
        PropTypes.shape({
          level_of_evidence: PropTypes.string,
          alterations: PropTypes.arrayOf(PropTypes.string),
          tumor_type: PropTypes.shape({
            name: PropTypes.string,
          }),
          pmids: PropTypes.arrayOf(PropTypes.string),
        })
      ),
    })
  ),
  variantGeneralInfo: PropTypes.shape({
    hgvsCdot: PropTypes.string,
    pValue: PropTypes.string,
  }).isRequired,
  isFLT3ITD: PropTypes.bool,
};

OncoKBVariant.defaultProps = {
  oncoKBData: [],
  isFLT3ITD: false,
};

const mapStateToProps = (state) => {
  return {
    oncoKBData: getOncoKBData(state),
    variantGeneralInfo: getVariantGeneralInfo(state),
    isFLT3ITD: getIsFLT3ITD(state),
  };
};

export default connect(mapStateToProps)(
  withErrorGuard(OncoKBVariant, "OncoKBVariant", "OncoKB")
);
